import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import masterroleApi from "../../../../../services/managelicense";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../../../../redux/loader/actions";
import { getDecryptedCookie } from "../../../../../utils";
import { makeStyles } from "@mui/styles";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { useParams } from "react-router-dom";

const useStyles = makeStyles({
  legend: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "10px",
  },
  checkIcon: {
    color: "green",
  },
  readIcon: {
    color: "orange",
  },
  noAccessIcon: {
    color: "red",
  },
});

const RoleChartDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { productid, busUid, businesssuuid } = useParams();
  const proid = getDecryptedCookie("proid") || productid;
  const classes = useStyles();
  const [roleChart, setRoleChart] = useState([]);

  const fetchRoleChart = async (proid) => {
    try {
      dispatch(showLoader());
      const res = await masterroleApi.getProRole(proid);
      const data = res.data.data.data;

      if (!Array.isArray(data)) {
        throw new Error("Invalid response: Expected an array");
      }

      setRoleChart(data);
    } catch (err) {
      console.error("Failed to fetch role chart", err);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    if (open) {
      fetchRoleChart(proid);
    }
  }, [open]);

  const renderAccessIcon = (accessType) => {
    if (accessType === "read_write") {
      return <CheckOutlinedIcon style={{ color: "green" }} />;
    } else if (accessType === "read") {
      return <CheckOutlinedIcon style={{ color: "orange" }} />;
    } else if (accessType === "none") {
      return <CloseOutlinedIcon style={{ color: "red" }} />;
    }
  };

  const getAllMenus = () => {
    const menus = new Map();

    roleChart.forEach((role) => {
      role.menus.forEach((menu) => {
        if (!menus.has(menu.menu_id)) {
          menus.set(menu.menu_id, { ...menu, parentName: menu.name });
        }

        if (menu.submenus) {
          menu.submenus.forEach((submenu) => {
            if (!menus.has(submenu.menu_id)) {
              menus.set(submenu.menu_id, {
                ...submenu,
                isSubmenu: true,
                parentName: menu.name,
              });
            }
          });
        }
      });
    });

    return Array.from(menus.values());
  };

  const allMenus = getAllMenus();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Role Chart
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.legend}>
          <Box display="flex" alignItems="center">
            <CheckOutlinedIcon className={classes.checkIcon} />
            <Typography variant="body2" sx={{ marginLeft: 1 }}>
              Full Access
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <CheckOutlinedIcon className={classes.readIcon} />
            <Typography variant="body2" sx={{ marginLeft: 1 }}>
              Read Access
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <CloseOutlinedIcon className={classes.noAccessIcon} />
            <Typography variant="body2" sx={{ marginLeft: 1 }}>
              No Access
            </Typography>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Menu</TableCell>
                {roleChart.map((role) => (
                  <TableCell key={role.role.role} align="center">
                    {role.role.role.charAt(0).toUpperCase() +
                      role.role.role.slice(1)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allMenus.map((menu) => (
                <TableRow key={menu.menu_id}>
                  <TableCell
                    style={{ paddingLeft: menu.isSubmenu ? "40px" : "10px" }}
                  >
                    {menu.isSubmenu ? (
                      <>
                        <SubdirectoryArrowRightIcon
                          style={{ marginRight: "5px" }}
                        />
                        {menu.name}
                      </>
                    ) : (
                      menu.name
                    )}
                  </TableCell>

                  {roleChart.map((role) => {
                    let accessType = "none";
                    if (menu.isSubmenu) {
                      role.menus.forEach((rMenu) => {
                        const submenu = rMenu.submenus?.find(
                          (sub) => sub.menu_id === menu.menu_id
                        );
                        if (submenu) {
                          accessType = submenu.access_type;
                        }
                      });
                    } else {
                      const roleMenu = role.menus.find(
                        (rMenu) => rMenu.menu_id === menu.menu_id
                      );
                      accessType = roleMenu?.access_type || "none";
                    }

                    return (
                      <TableCell key={role.role.role} align="center">
                        {renderAccessIcon(accessType)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default RoleChartDialog;
